// Settings Component - Simplified for Single-User Admin System
// No roles needed - user is always admin with full permissions

import React, { useState, useEffect } from "react";
import { message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { 
  AdjustmentsIcon,
  UserIcon,
  CreditCardIcon,
  MailIcon,
  OfficeBuildingIcon,
  CogIcon,
  ShoppingCartIcon,
} from '@heroicons/react/outline';
import { 
  Button,
  Modal,
  LoadingState,
  colors,
  typography,
  spacing,
} from "../../design-system";
import { Card, Empty, Statistic } from 'antd';
import { BillingSection } from "./BillingSection";
import { ChannelsSection } from "./ChannelsSection";
import { OrganizationSection } from "./OrganizationSection";
import { ProfileSection } from "./ProfileSection";
import { userService } from "../../services/userService";
import { shopifyStatusService } from "../../services/shopifyStatusService";
import { billingService, BillingInfo } from "../../services/billingService";
import { channelService } from "../../services/channelService";
import * as S from "./Settings.styles";

// Styled replacements for deleted design system components
const PageContainer = styled.div`
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
`;
interface SettingItem {
  id: string;
  icon: React.ReactNode;
  name: string;
  description: string;
  status: string;
  statusType: 'success' | 'warning' | 'error' | 'neutral';
  action: () => void;
  actionLabel: string;
}

const Settings: React.FC = () => {
  const [organizationData, setOrganizationData] = useState<{
    name: string;
    teamMembersCount: number;
  } | null>(null);
  const [channels, setChannels] = useState<any[]>([]);
  const [shopifyStatus, setShopifyStatus] = useState<{ isConnected: boolean; display?: string } | null>(null);
  const [requestsRemaining, setRequestsRemaining] = useState<number | null>(null);
  const [refreshingTokens, setRefreshingTokens] = useState<boolean>(false);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  
  // Modal states for each card action
  const [showChannelsModal, setShowChannelsModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionStart = new Date().toISOString();
        const [user, channelsData, billing, shopify] = await Promise.all([
          userService.getCurrentUser(),
          channelService.getAllChannels().catch(() => []), // Don't fail if channels fail
          billingService.getCurrentSubscription().catch(() => null),
          shopifyStatusService.getStatus(sessionStart).catch(() => null)
        ]);
        if (shopify && typeof shopify === 'object') {
          setShopifyStatus({
            isConnected: Boolean((shopify as any).isConnected),
            display: (shopify as any).shop_domain || (shopify as any).display_name,
          });
        }
        
        setChannels(Array.isArray(channelsData) ? channelsData : []);
        setChannelsLoading(false);
        
        // Set organization data - single user system
        if (user?.organization_name) {
          setOrganizationData({
            name: user.organization_name || '',
            teamMembersCount: 1, // Single user system
          });
        }
        if (user?.id) {
          setCurrentUserId(user.id);
        }

        // Store billing info
        if (billing) {
          setBillingInfo(billing);
          // Capture requests remaining from backend if present
          if (typeof (billing as any).requests_remaining === 'number') {
            setRequestsRemaining((billing as any).requests_remaining);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setChannelsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChannelsUpdated = async () => {
    try {
      const channelsData = await channelService.getAllChannels();
      setChannels(Array.isArray(channelsData) ? channelsData : []);
    } catch (error) {
      console.error("Error loading channels:", error);
      setChannels([]); // Ensure it's always an array on error
    }
  };

  const handleOrganizationUpdated = async () => {
    // Refetch organization data after update
    try {
      const user = await userService.getCurrentUser();
      
      if (user?.organization_name) {
        setOrganizationData({
          name: user.organization_name || '',
          teamMembersCount: 1, // Single user system
        });
      }
    } catch (error) {
      console.error("Error refetching organization data:", error);
    }
  };

  const hasOutlookChannel = Array.isArray(channels) && channels.some(channel => channel.channel_type === 'email');
  const hasShopifyChannel = shopifyStatus ? shopifyStatus.isConnected : (Array.isArray(channels) && channels.some((channel: any) => channel.channel_type === 'shopify' || channel.type === 'shopify'));
  const shopifyChannel = shopifyStatus?.display
    ? { identifier: shopifyStatus.display }
    : (Array.isArray(channels)
      ? (channels.find((c: any) => c.channel_type === 'shopify' || c.type === 'shopify') || null)
      : null);

  const disconnectShopify = async () => {
    try {
      if (!shopifyChannel?.identifier) {
        message.error('No Shopify store connected');
        return;
      }
      const confirmed = window.confirm(`Disconnect ${shopifyChannel.identifier}? This removes local access until you reinstall.`);
      if (!confirmed) return;
      const { channelService } = await import('../../services/channelService');
      await channelService.disconnectShopify(shopifyChannel.identifier);
      message.success('Shopify disconnected locally');
      // Update both channels and shopify status after disconnecting
      setShopifyStatus({ isConnected: false, display: undefined });
      await handleChannelsUpdated();
    } catch (e: any) {
      console.error('Disconnect Shopify failed', e);
      message.error(e?.message || 'Failed to disconnect Shopify');
    }
  };

  // Calculate stats
  const connectedChannels = hasOutlookChannel ? 1 : 0;
  const totalChannels = 1; // Email only

  // Get billing status display
  const getBillingStatus = () => {
    if (!billingInfo) return 'Not configured';
    
    switch (billingInfo.status) {
      case 'active': return 'Active';
      case 'trialing': return `Trial (${billingInfo.trial_days_remaining || 0} days left)`;
      case 'expired': return 'No Plan';
      default: return 'No Plan';
    }
  };

  const getBillingStatusType = (): 'success' | 'warning' | 'error' | 'neutral' => {
    if (!billingInfo) return 'neutral';
    
    switch (billingInfo.status) {
      case 'active': return 'success';
      case 'trialing': return 'warning';
      case 'expired': return 'error';
      default: return 'neutral';
    }
  };

  // Generate settings items
  const settingsItems: SettingItem[] = [
    {
      id: 'profile',
      icon: <UserIcon style={{ width: '20px', height: '20px' }} />,
      name: 'Profile',
      description: 'Manage your profile image and personal information',
      status: 'Configured',
      statusType: 'success',
      action: () => setShowProfileModal(true),
      actionLabel: 'Manage'
    },
    {
      id: 'shopify',
      icon: <ShoppingCartIcon style={{ width: '20px', height: '20px' }} />,
      name: 'Shopify',
      description: hasShopifyChannel 
        ? 'Connect your Shopify store to manage discounts and analyze orders'
        : 'Install from Shopify App Store to manage discounts and analyze orders',
      status: hasShopifyChannel 
        ? (channels.find((c: any) => c.channel_type === 'shopify' || c.type === 'shopify')?.identifier || 'Connected')
        : 'Not connected',
      statusType: hasShopifyChannel ? 'success' : 'neutral',
      action: hasShopifyChannel ? disconnectShopify : () => {},
      actionLabel: hasShopifyChannel ? 'Disconnect' : ''
    },
    {
      id: 'organization',
      icon: <OfficeBuildingIcon style={{ width: '20px', height: '20px' }} />,
      name: 'Organization',
      description: 'Manage organization details and team settings',
      status: organizationData?.name || 'Not configured',
      statusType: organizationData ? 'success' : 'warning',
      action: () => setShowOrgModal(true),
      actionLabel: 'Manage'
    },
    {
      id: 'email-channel',
      icon: <MailIcon style={{ width: '20px', height: '20px' }} />,
      name: 'Email Channel',
      description: 'Connect and manage your email account for conversations',
      status: hasOutlookChannel 
        ? channels.find(c => c.channel_type === 'email')?.identifier || 'Connected'
        : 'Not connected',
      statusType: hasOutlookChannel ? 'success' : 'error',
      action: () => setShowChannelsModal(true),
      actionLabel: 'Configure'
    },
    {
      id: 'billing',
      icon: <CreditCardIcon style={{ width: '20px', height: '20px' }} />,
      name: 'Billing & Subscription',
      description: 'Manage your subscription plan and billing details',
      status: getBillingStatus(),
      statusType: getBillingStatusType(),
      action: () => setShowBillingModal(true),
      actionLabel: 'Manage'
    }
  ];

  return (
    <PageContainer>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing[6] }}>
        <h1 style={{ margin: 0, fontSize: typography.fontSizes['2xl'], fontWeight: typography.fontWeights.semibold, color: colors.text.primary }}>
          Settings
        </h1>
        {organizationData && (
          <Button
            variant="primary"
            icon={<UserIcon style={{ width: '14px', height: '14px' }} />}
            onClick={() => setShowOrgModal(true)}
          >
            Manage Organization
          </Button>
        )}
      </div>

      {/* Stats */}
      <StatsGrid>
        <Card>
          <Statistic
            title="Connected Channels"
            value={`${connectedChannels}/${totalChannels}`}
          />
        </Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Card>
            <Statistic
              title="AI Tokens"
              value={
                billingInfo?.status === 'trialing' 
                  ? 'Unlimited (Trial)' 
                  : typeof requestsRemaining === 'number' 
                    ? `${requestsRemaining}/100` 
                    : '100/100'
              }
            />
          </Card>
          <button
            onClick={async () => {
              if (refreshingTokens) return;
              try {
                setRefreshingTokens(true);
                // Clear cache and fetch fresh subscription to update tokens
                // Import billingService lazily to avoid circular imports
                const { billingService } = await import('../../services/billingService');
                billingService.clearCache();
                const refreshed = await billingService.getCurrentSubscription();
                if (refreshed) {
                  setBillingInfo(refreshed);
                  if (typeof (refreshed as any).requests_remaining === 'number') {
                    setRequestsRemaining((refreshed as any).requests_remaining);
                  }
                }
                // message.success('AI tokens refreshed');
              } catch (e) {
                // message.error('Failed to refresh AI tokens');
              } finally {
                setRefreshingTokens(false);
              }
            }}
            disabled={refreshingTokens}
            style={{
              padding: '6px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              background: '#fff',
              cursor: refreshingTokens ? 'not-allowed' : 'pointer'
            }}
            title="Refresh AI tokens"
          >
            <ReloadOutlined spin={refreshingTokens} />
          </button>
        </div>
        <Card>
          <Statistic
            title="Organization"
            value={organizationData?.name || 'Not set'}
          />
        </Card>
        <Card>
          <Statistic
            title="Team Members"
            value={organizationData?.teamMembersCount || 0}
          />
        </Card>
      </StatsGrid>

      {/* Settings Table */}
      <div style={{ marginTop: '32px' }}>
        {channelsLoading ? (
          <LoadingState />
        ) : settingsItems.length === 0 ? (
          <Empty 
            description="Settings could not be loaded."
            image={<CogIcon style={{ width: '48px', height: '48px', color: colors.text.tertiary }} />}
          />
        ) : (
          <S.SettingsTable>
            {settingsItems.map((setting) => (
              <S.SettingsRow key={setting.id}>
                <S.SettingIconContainer>
                  {setting.icon}
                </S.SettingIconContainer>
                
                <S.SettingContent>
                  <S.SettingName>{setting.name}</S.SettingName>
                  <S.SettingDescription>{setting.description}</S.SettingDescription>
                </S.SettingContent>
                
                <S.SettingStatus statusType={setting.statusType}>
                  {setting.status}
                </S.SettingStatus>
                
                <S.SettingActions>
                  {setting.actionLabel && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<CogIcon style={{ width: '14px', height: '14px' }} />}
                      onClick={setting.action}
                    >
                      {setting.actionLabel}
                    </Button>
                  )}
                </S.SettingActions>
              </S.SettingsRow>
            ))}
          </S.SettingsTable>
        )}
      </div>

      {/* Modals */}
      <Modal 
        title="Channel Management" 
        isOpen={showChannelsModal} 
        onClose={() => setShowChannelsModal(false)}
        size="lg"
      >
        <ChannelsSection />
      </Modal>

      <Modal 
        title="Organization Settings" 
        isOpen={showOrgModal} 
        onClose={() => setShowOrgModal(false)}
        size="lg"
      >
        {organizationData && (
          <OrganizationSection
            organizationName={organizationData.name}
            teamMembersCount={organizationData.teamMembersCount}
            onOrganizationUpdated={handleOrganizationUpdated}
          />
        )}
      </Modal>

      <Modal 
        title="Billing & Subscription" 
        isOpen={showBillingModal} 
        onClose={() => setShowBillingModal(false)}
        size="lg"
      >
        <BillingSection />
      </Modal>

      <Modal 
        title="Profile Settings" 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)}
        size="lg"
      >
        <ProfileSection />
      </Modal>

    </PageContainer>
  );
};

export default Settings;
